import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchCoffeeBySlug, CoffeeRecord } from '../utils/api';
import { useSampleOrderModal } from '../App';
import { usePageMeta } from '../utils/usePageMeta';
import { CoffeeProductSchema } from '../components/StructuredData';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';

// Parses "**Label:** content **Label:** content" or one "Label: content" per line into labeled sections
function renderBoldSections(text: string) {
  let parts: { label: string; content: string }[] = [];

  const regex = /\*\*([^*]+?):\*\*\s*([\s\S]*?)(?=\s*\*\*[^*]+?:\*\*|$)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const content = match[2].trim();
    if (content) parts.push({ label: match[1].trim(), content });
  }

  if (parts.length === 0) {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const lineParts = lines.map(line => {
      const m = line.match(/^([A-Za-z][A-Za-z ]{2,30}):\s*(.+)$/);
      return m ? { label: m[1].trim(), content: m[2].trim() } : null;
    });
    if (lines.length > 0 && lineParts.every(Boolean)) {
      parts = lineParts as { label: string; content: string }[];
    }
  }

  if (parts.length === 0) {
    return <p className="font-display text-lg text-[#4A4A4A] leading-[1.7]">{text}</p>;
  }

  return (
    <div className="space-y-5">
      {parts.map(({ label, content }) => (
        <div key={label}>
          <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">{label}</p>
          <p className="font-display text-lg text-[#4A4A4A] leading-[1.6]">{content}</p>
        </div>
      ))}
    </div>
  );
}

// Orders grade rows consistently, with unrecognized grades (e.g. TT) last
const GRADE_ORDER = ['AA', 'AB', 'PB/C', 'PB', 'C'];
function sortedGradeKeys(a: Record<string, number>, b: Record<string, number>) {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  return keys.sort((x, y) => {
    const ix = GRADE_ORDER.indexOf(x);
    const iy = GRADE_ORDER.indexOf(y);
    if (ix === -1 && iy === -1) return x.localeCompare(y);
    if (ix === -1) return 1;
    if (iy === -1) return -1;
    return ix - iy;
  });
}

// Renders "30kg. GrainPro. Jute bags" as clean dot-separated inline values
function renderPackaging(text: string) {
  const parts = text.split('.').map(s => s.trim()).filter(Boolean);
  if (parts.length <= 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1.5 text-[#A3A3A3]">·</span>}
          {part}
        </React.Fragment>
      ))}
    </>
  );
}

export const CoffeeDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { openModal } = useSampleOrderModal();
  const [coffee, setCoffee] = useState<CoffeeRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setError('No coffee specified'); setIsLoading(false); return; }
    fetchCoffeeBySlug(slug)
      .then(data => { data ? setCoffee(data) : setError('Coffee not found'); })
      .catch(() => setError('Failed to load coffee details'))
      .finally(() => setIsLoading(false));
  }, [slug]);

  usePageMeta({
    title: coffee ? `${coffee.title} — ${coffee.processType} | Lot 20 Coffee` : 'Coffee Details | Lot 20',
    description: coffee
      ? `${coffee.title}: ${coffee.tastingNotes}. ${coffee.processType} processed Kenyan specialty coffee.`
      : 'Explore our Kenyan specialty coffee details and flavor profiles.',
    canonicalPath: slug ? `/coffee/${slug}` : undefined,
    ogImage: coffee?.imageUrl,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <p className="text-sm text-[#A3A3A3]">Loading...</p>
      </div>
    );
  }

  if (error || !coffee) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8] px-6">
        <h1 className="text-2xl font-display text-[#404040] mb-3">Coffee not found</h1>
        <p className="text-[#6B6B6B] mb-6">{error}</p>
        <Link to="/coffees" className="text-sm text-[#E85C27] hover:underline">&larr; Back to coffees</Link>
      </div>
    );
  }

  return (
    <>
      <CoffeeProductSchema
        name={coffee.title}
        description={coffee.visualDescription || coffee.tastingNotes}
        imageUrl={coffee.imageUrl}
        price={coffee.pricePerKg ? parseFloat(coffee.pricePerKg) : undefined}
        availability={coffee.availabilityStatus}
        slug={coffee.slug}
      />

      {/* Hero */}
      <section className="relative bg-[#404040] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-20">
          <Link to="/coffees" className="text-xs text-white/60 hover:text-white transition-colors mb-8 inline-block">
            &larr; All Coffees
          </Link>
          <div className="grid md:grid-cols-[340px_1fr] lg:grid-cols-[400px_1fr] gap-10 lg:gap-16 items-end">
            <div className="aspect-[4/5] overflow-hidden bg-white/5 shrink-0">
              {coffee.imageUrl ? (
                <img
                  src={coffee.imageUrl}
                  alt={coffee.title}
                  width={800}
                  height={1000}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <span className="text-4xl font-display text-white/20">LOT 20</span>
                  <span className="text-xs uppercase tracking-[0.3em] text-white/20 mt-3">{coffee.processType}</span>
                </div>
              )}
            </div>
            <div>
              <Eyebrow tone="onDark" className="mb-3">{coffee.processType}</Eyebrow>
              <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.05] mb-6">{coffee.title}</h1>

              {coffee.visualDescription && (
                <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-xl mb-6">
                  {coffee.visualDescription}
                </p>
              )}

              {coffee.tastingNotes && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {coffee.tastingNotes.split(',').map(note => note.trim()).filter(Boolean).map(note => (
                    <span
                      key={note}
                      className="px-3 py-1.5 border border-white/20 text-white/80 text-xs uppercase tracking-[0.1em]"
                    >
                      {note}
                    </span>
                  ))}
                </div>
              )}

              {(coffee.cuppingScore > 0 || coffee.altitude || coffee.location || coffee.region) && (
                <div className="flex flex-wrap gap-x-10 gap-y-4 pt-6 border-t border-white/10">
                  {coffee.cuppingScore > 0 && (
                    <div>
                      <p className="font-display text-2xl text-white">{coffee.cuppingScore}</p>
                      <Eyebrow tone="onDark" size="sm" className="mt-1">Cupping Score</Eyebrow>
                    </div>
                  )}
                  {coffee.altitude && (
                    <div>
                      <p className="font-display text-2xl text-white">{coffee.altitude}</p>
                      <Eyebrow tone="onDark" size="sm" className="mt-1">Altitude</Eyebrow>
                    </div>
                  )}
                  {(coffee.location || coffee.region) && (
                    <div>
                      <p className="font-display text-2xl text-white">{coffee.location || coffee.region}</p>
                      <Eyebrow tone="onDark" size="sm" className="mt-1">Origin</Eyebrow>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content: Two Column */}
      <section className="py-16 md:py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-[1fr_360px] gap-16 lg:gap-24">
            {/* Left — Story */}
            <div>
              {/* In the Cup — leading pull paragraph */}
              {coffee.detailedDescription && (
                <div className="mb-14 pb-14 border-b border-[#E8E6E2]">
                  <Eyebrow className="mb-4">In the Cup</Eyebrow>
                  <p className="font-display font-normal text-2xl md:text-[1.75rem] text-[#404040] leading-[1.4] max-w-2xl">
                    {coffee.detailedDescription}
                  </p>
                </div>
              )}

              {/* Processing */}
              {(coffee.fermentationMethod || coffee.fermentationDetails || coffee.dryingMethod) && (
                <div className="mb-14 pb-14 border-b border-[#E8E6E2]">
                  <Eyebrow className="mb-6">Processing</Eyebrow>
                  <div className="space-y-7">
                    {coffee.fermentationMethod && (
                      <div className="border-l-2 border-[#E8E6E2] pl-6">
                        <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">Fermentation</p>
                        <p className="font-display text-lg text-[#404040]">{coffee.fermentationMethod}</p>
                        {coffee.fermentationDuration && (
                          <p className="font-display text-[#6B6B6B] mt-1">Duration: {coffee.fermentationDuration}</p>
                        )}
                      </div>
                    )}
                    {coffee.fermentationDetails && (
                      <div className="border-l-2 border-[#E8E6E2] pl-6">
                        <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">Details</p>
                        {renderBoldSections(coffee.fermentationDetails)}
                      </div>
                    )}
                    {/* Skip if Details already covers drying, to avoid repeating it */}
                    {coffee.dryingMethod && !/drying/i.test(coffee.fermentationDetails || '') && (
                      <div className="border-l-2 border-[#E8E6E2] pl-6">
                        <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">Drying</p>
                        <p className="font-display text-lg text-[#404040]">
                          {coffee.dryingMethod}
                          {coffee.dryingDetails ? ` · ${coffee.dryingDetails}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Producer */}
              {coffee.productionInformation && (
                <div>
                  <Eyebrow className="mb-5">Producer</Eyebrow>
                  <div className="space-y-5 max-w-2xl">
                    {coffee.productionInformation.split(/\n\n+/).map((para, i) => (
                      <p key={i} className="font-display text-lg text-[#4A4A4A] leading-[1.7]">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right — Specs Sidebar */}
            <div className="lg:sticky lg:top-28 self-start">
              <div className="bg-white border border-[#E8E6E2] p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <Eyebrow>Specifications</Eyebrow>
                  {coffee.cuppingScore > 0 && (
                    <span className="text-sm font-medium text-[#404040]">{coffee.cuppingScore} pts</span>
                  )}
                </div>

                <div className="border-t border-[#E8E6E2] pt-5 space-y-4">
                  {[
                    { label: 'Process', value: coffee.processType },
                    { label: 'Region', value: coffee.region },
                    { label: 'Location', value: coffee.location },
                    { label: 'Altitude', value: coffee.altitude },
                    { label: 'Varieties', value: coffee.varieties },
                    { label: 'Harvest', value: coffee.harvestPeriod },
                    { label: 'Screen Size', value: coffee.screenSize },
                    { label: 'Available', value: coffee.quantityInStock },
                    { label: 'Packaging', value: coffee.packagingInfo, render: renderPackaging },
                    {
                      label: 'Price/kg',
                      value: (!coffee.gradePricing || Object.keys(coffee.gradePricing).length === 0) && coffee.pricePerKg
                        ? `$${coffee.pricePerKg}`
                        : '',
                    },
                  ].filter(s => s.value).map(spec => (
                    <div key={spec.label} className="flex justify-between items-baseline">
                      <span className="text-xs uppercase tracking-wider text-[#A3A3A3]">{spec.label}</span>
                      <span className="text-sm text-[#404040] text-right max-w-[60%]">
                        {'render' in spec && spec.render ? spec.render(spec.value as string) : spec.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Grade Breakdown */}
                {((coffee.gradeBreakdown && Object.keys(coffee.gradeBreakdown).length > 0) ||
                  (coffee.gradePricing && Object.keys(coffee.gradePricing).length > 0)) && (() => {
                  const grades = sortedGradeKeys(coffee.gradeBreakdown || {}, coffee.gradePricing || {});
                  const rows = grades.map(grade => ({
                    grade,
                    kg: coffee.gradeBreakdown?.[grade],
                    price: coffee.gradePricing?.[grade],
                  }));

                  return (
                    <div className="border-t border-[#E8E6E2] pt-5">
                      <Eyebrow className="mb-4">Grade Breakdown</Eyebrow>
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11px] uppercase tracking-wider text-[#A3A3A3] w-10">Grade</span>
                          <span className="text-[11px] uppercase tracking-wider text-[#A3A3A3] flex-1 text-right pr-4">Available</span>
                          <span className="text-[11px] uppercase tracking-wider text-[#A3A3A3] w-16 text-right">Price/kg</span>
                        </div>
                        {rows.map(({ grade, kg, price }) => (
                          <div key={grade} className="flex justify-between items-baseline">
                            <span className="text-sm font-medium text-[#404040] w-10">{grade}</span>
                            <span className="text-sm text-[#6B6B6B] tabular-nums flex-1 text-right pr-4">
                              {kg ? `${Number(kg).toLocaleString()} kg` : '—'}
                            </span>
                            <span className="text-sm font-medium text-[#404040] tabular-nums w-16 text-right">
                              {price ? `$${price}` : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Status */}
                <div className="border-t border-[#E8E6E2] pt-5">
                  <div className="flex items-center gap-2 mb-5">
                    <span className={`w-2 h-2 rounded-full ${
                      coffee.availabilityStatus === 'AVAILABLE' ? 'bg-green-500' :
                      coffee.availabilityStatus === 'PREORDER' ? 'bg-amber-500' : 'bg-[#A3A3A3]'
                    }`} />
                    <span className="text-xs uppercase tracking-wider text-[#6B6B6B]">
                      {coffee.availabilityStatus}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={() => openModal()}
                      variant="primary"
                      size="lg"
                      className="w-full"
                    >
                      Request Samples
                    </Button>
                    <Button
                      to={`/contact?coffee=${coffee.slug}`}
                      variant="outline"
                      size="lg"
                      className="w-full"
                    >
                      Make Inquiry
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
